import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Save,
  Plus,
  Trash2,
  Loader2,
  AlertTriangle,
  Clock,
  MapPin,
  MessageCircle,
  Copy,
  ExternalLink,
  Pencil,
  Sparkles,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import WeeklySlotsEditor, { emptySlots } from "./WeeklySlotsEditor";
import PlannerDialog from "./PlannerDialog";
import StudentClassChronogram from "./StudentClassChronogram";
import QuickAddDialog, { QUICK_ADD_VALUE } from "@/components/autoescuela/QuickAddDialog";

// Reusable helpers
const todayISO = () => new Date().toISOString().slice(0, 10);
const isActiveIncident = (i) => {
  const t = todayISO();
  return i.date_start <= t && t <= i.date_end;
};
const normalizePhoneWA = (phone) => {
  let p = String(phone || "").replace(/\D/g, "");
  if (!p) return "";
  if (!p.startsWith("34")) p = "34" + p;
  return p;
};

function IncidentRow({ inc, onEdit, onDelete }) {
  const active = isActiveIncident(inc);
  return (
    <div
      className={
        "rounded-xl border px-3 py-2 flex items-start gap-2 " +
        (active ? "border-amber-300 bg-amber-50" : "border-neutral-200 bg-white")
      }
      data-testid={`incident-row-${inc.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm">{inc.reason}</span>
          {active && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-amber-200 text-amber-900 rounded-full px-2 py-0.5">
              <AlertTriangle className="h-3 w-3" /> ACTIVA
            </span>
          )}
        </div>
        <div className="text-[11px] text-neutral-500 mt-0.5">
          {inc.date_start} → {inc.date_end}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onEdit(inc)}
          className="p-1.5 rounded hover:bg-neutral-100 text-neutral-600"
          data-testid={`incident-edit-${inc.id}`}
          title="Editar"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(inc)}
          className="p-1.5 rounded hover:bg-red-50 text-red-600"
          data-testid={`incident-delete-${inc.id}`}
          title="Eliminar"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function IncidentForm({ initial, onCancel, onSave, saving }) {
  const [reason, setReason] = useState(initial?.reason || "");
  const [ds, setDs] = useState(initial?.date_start || todayISO());
  const [de, setDe] = useState(initial?.date_end || todayISO());

  const submit = () => {
    if (!reason.trim()) { toast.error("El motivo es obligatorio."); return; }
    if (!ds || !de) { toast.error("Fecha inicio y fecha fin son obligatorias."); return; }
    if (de < ds) { toast.error("La fecha fin no puede ser anterior a la fecha inicio."); return; }
    onSave({ reason: reason.trim(), date_start: ds, date_end: de });
  };

  return (
    <div className="rounded-xl border border-orange-200 bg-orange-50/50 p-3 space-y-2" data-testid="incident-form">
      <input
        type="text"
        placeholder="Motivo (ej. Exámenes universitarios, Vacaciones, Baja médica...)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        data-testid="incident-reason"
        autoFocus
        maxLength={300}
      />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-0.5">Desde</label>
          <input type="date" value={ds} onChange={(e) => setDs(e.target.value)} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-2 text-sm" data-testid="incident-date-start" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-0.5">Hasta</label>
          <input type="date" value={de} onChange={(e) => setDe(e.target.value)} className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-2 text-sm" data-testid="incident-date-end" />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn-secondary !h-9 !px-3 text-xs">Cancelar</button>
        <button type="button" onClick={submit} disabled={saving} className="btn-primary !h-9 !px-3 text-xs" data-testid="incident-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Guardar
        </button>
      </div>
    </div>
  );
}

export default function StudentSchedulingTab({ student }) {
  const [availability, setAvailability] = useState(null);
  const [slots, setSlots] = useState(emptySlots());
  const [prefs, setPrefs] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [pickupId, setPickupId] = useState("");
  const [locations, setLocations] = useState([]);
  const [quickAdd, setQuickAdd] = useState(false);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addingIncident, setAddingIncident] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);
  const [savingIncident, setSavingIncident] = useState(false);
  const [wa, setWa] = useState(null); // {portal_url, wa_link}
  const [creatingWa, setCreatingWa] = useState(false);
  const [plannerOpen, setPlannerOpen] = useState(false);

  const sid = student?.id;

  const load = useCallback(async () => {
    if (!sid) return;
    setLoading(true);
    try {
      const [a, inc, locs] = await Promise.all([
        api.get(`/students/${sid}/availability`),
        api.get(`/students/${sid}/incidents`),
        api.get(`/locations`).catch(() => ({ data: [] })),
      ]);
      setAvailability(a.data);
      setSlots(a.data.weekly_slots || emptySlots());
      setPrefs(a.data.preferences || "");
      setNeighborhood(a.data.neighborhood || "");
      setPickupId(a.data.habitual_pickup_location_id || "");
      setIncidents(Array.isArray(inc.data) ? inc.data : []);
      setLocations(Array.isArray(locs.data) ? locs.data : []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [sid]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/students/${sid}/availability`, {
        weekly_slots: slots,
        preferences: prefs,
        neighborhood,
        habitual_pickup_location_id: pickupId,
      });
      setAvailability(data);
      toast.success("Horarios guardados.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const submitIncident = async (payload) => {
    setSavingIncident(true);
    try {
      if (editingIncident?.id) {
        await api.patch(`/students/${sid}/incidents/${editingIncident.id}`, payload);
      } else {
        await api.post(`/students/${sid}/incidents`, payload);
      }
      setAddingIncident(false);
      setEditingIncident(null);
      const { data } = await api.get(`/students/${sid}/incidents`);
      setIncidents(data || []);
      toast.success("Incidencia guardada.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSavingIncident(false);
    }
  };

  const deleteIncident = async (inc) => {
    if (!window.confirm(`¿Eliminar incidencia "${inc.reason}"?`)) return;
    try {
      await api.delete(`/students/${sid}/incidents/${inc.id}`);
      setIncidents(incidents.filter((i) => i.id !== inc.id));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const createWaQuestionnaire = async () => {
    setCreatingWa(true);
    try {
      // V1.54 · Regenerar: el enlace anterior se cancela (nunca se repite).
      if (wa?.id) {
        await api.post("/secure-links/cancel", { kind: "cuestionario_horarios", id: wa.id }).catch(() => {});
      }
      const { data } = await api.post(`/students/${sid}/availability-questionnaire`);
      const p = normalizePhoneWA(student?.phone);
      const msg =
        `Hola ${student?.name}, para poder programarte mejor las clases necesitamos ` +
        `conocer tu disponibilidad y posibles incidencias.\n\nRellena este breve ` +
        `formulario (menos de 2 minutos):\n${data.portal_url}\n\n¡Gracias!`;
      const waLink = p ? `https://wa.me/${p}?text=${encodeURIComponent(msg)}` : "";
      setWa({
        id: data.id,
        portal_url: data.portal_url,
        wa_link: waLink,
        created_at: data.created_at,
        expires_at: data.expires_at,
        expires_hours: data.expires_hours,
      });
      toast.success("Enlace único del cuestionario generado.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setCreatingWa(false);
    }
  };

  const cancelWaLink = async () => {
    if (!wa?.id) return;
    try {
      await api.post("/secure-links/cancel", { kind: "cuestionario_horarios", id: wa.id });
      setWa(null);
      toast.success("Enlace cancelado: ya no es accesible.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Enlace copiado."); }
    catch { toast.error("No se pudo copiar."); }
  };

  if (loading || !availability) {
    return <div className="py-8 text-center text-sm text-neutral-500"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Cargando…</div>;
  }

  return (
    <div className="space-y-5" data-testid="student-scheduling-tab">
      {/* AI Planner CTA */}
      <div className="rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-50 via-white to-red-50 p-4">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="text-xs font-bold uppercase tracking-wider text-orange-700 mb-1 flex items-center gap-1">
              <Sparkles className="h-3.5 w-3.5" /> Kai Planificador
            </div>
            <p className="text-sm text-neutral-700 leading-relaxed">
              Claude Sonnet 4.5 analiza disponibilidad, incidencias, zona y profesores para proponerte 3 clases óptimas. <b>La IA propone, tú decides.</b>
            </p>
          </div>
          <button
            type="button"
            onClick={() => setPlannerOpen(true)}
            className="inline-flex items-center gap-2 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold px-4 text-sm shadow-md"
            data-testid="open-planner"
          >
            <Sparkles className="h-4 w-4" /> Sugerir próxima clase
          </button>
        </div>
      </div>

      {/* WhatsApp questionnaire card */}
      <div className="rounded-2xl border border-green-200 bg-gradient-to-br from-green-50 to-white p-4">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="text-xs font-bold uppercase tracking-wider text-green-700 mb-1">Cuestionario por WhatsApp</div>
            <p className="text-sm text-neutral-700 leading-relaxed">
              Envía al alumno un enlace para que rellene su disponibilidad, incidencias y preferencias.
              Al enviar, la información se guarda automáticamente en su ficha.
            </p>
          </div>
          <button
            type="button"
            onClick={createWaQuestionnaire}
            disabled={creatingWa}
            className="inline-flex items-center gap-2 h-10 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold px-4 text-sm shadow-sm disabled:opacity-50"
            data-testid="create-wa-questionnaire"
          >
            {creatingWa ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
            {wa ? "Regenerar enlace" : "Generar enlace"}
          </button>
        </div>
        {wa && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <a href={wa.portal_url} target="_blank" rel="noopener noreferrer" data-testid="wa-portal-url" className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50 px-2.5 py-1 text-[11px] font-semibold text-neutral-700 max-w-[240px] truncate">
                <ExternalLink className="h-3 w-3" />{wa.portal_url.replace(/^https?:\/\//, "").slice(0, 40)}…
              </a>
              <button type="button" onClick={() => copyLink(wa.portal_url)} className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50 px-2.5 py-1 text-[11px] font-semibold" data-testid="wa-copy-link">
                <Copy className="h-3 w-3" /> Copiar
              </button>
              {wa.wa_link && (
                <a href={wa.wa_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-green-200 bg-green-50 hover:bg-green-100 px-2.5 py-1 text-[11px] font-semibold text-green-800" data-testid="wa-send">
                  <MessageCircle className="h-3 w-3" /> Enviar por WhatsApp
                </a>
              )}
              <button type="button" onClick={cancelWaLink} className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 px-2.5 py-1 text-[11px] font-bold text-red-700" data-testid="wa-cancel-link">
                Cancelar enlace
              </button>
            </div>
            {wa.created_at && (
              <p className="text-[10px] text-neutral-500 font-mono tabular-nums" data-testid="wa-link-audit">
                Auditoría: creado {new Date(wa.created_at).toLocaleString("es-ES")} · caduca {new Date(wa.expires_at).toLocaleString("es-ES")} ({wa.expires_hours} h) · un solo uso, nunca se repite
              </p>
            )}
          </div>
        )}
      </div>

      {/* Weekly availability */}
      <section>
        <div className="flex items-center gap-2 mb-2">
          <Clock className="h-4 w-4 text-orange-500" />
          <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-500">Disponibilidad semanal</h3>
          {availability.updated_via && (
            <span className="text-[10px] text-neutral-400 ml-auto">
              Actualizado {availability.updated_via === "whatsapp_questionnaire" ? "vía WhatsApp" : "por gestor"}
              {availability.updated_at ? ` · ${availability.updated_at.slice(0, 10)}` : ""}
            </span>
          )}
        </div>
        <WeeklySlotsEditor value={slots} onChange={setSlots} testidPrefix="student-slot" />
      </section>

      {/* Preferences + geo */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">
            Preferencias horarias (texto libre)
          </label>
          <textarea
            value={prefs}
            onChange={(e) => setPrefs(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            placeholder="Ej: prefiero mañanas; los miércoles solo tarde; nunca sábados..."
            data-testid="preferences-input"
          />
        </div>
        <div className="space-y-2">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">
              <MapPin className="inline h-3 w-3 mr-1" /> Barrio
            </label>
            <input
              type="text"
              value={neighborhood}
              onChange={(e) => setNeighborhood(e.target.value)}
              className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Restón, Centro, Estación..."
              data-testid="neighborhood-input"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">
              Punto habitual de recogida
            </label>
            <select
              value={pickupId}
              onChange={(e) => (e.target.value === QUICK_ADD_VALUE ? setQuickAdd(true) : setPickupId(e.target.value))}
              className="w-full h-9 rounded-lg border border-neutral-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="pickup-select"
            >
              <option value="">— Sin definir —</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
              <option value={QUICK_ADD_VALUE}>+ Añadir ubicación nueva…</option>
            </select>
            <QuickAddDialog
              open={quickAdd}
              entity="location"
              onClose={() => setQuickAdd(false)}
              onCreated={async (doc) => {
                setQuickAdd(false);
                const { data } = await api.get("/locations");
                setLocations(Array.isArray(data) ? data : []);
                if (doc?.id) setPickupId(doc.id);
              }}
            />
            {availability.habitual_pickup_hint && !pickupId && (
              <div className="text-[10px] text-amber-700 mt-1">
                El alumno indicó: <b>"{availability.habitual_pickup_hint}"</b> — enlázalo a una ubicación.
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="btn-primary"
          data-testid="save-availability"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Guardar horarios y preferencias
        </button>
      </div>

      {/* Incidents */}
      <section className="pt-4 border-t border-neutral-100">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="h-4 w-4 text-orange-500" />
          <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-500">
            Incidencias temporales
          </h3>
          {!addingIncident && !editingIncident && (
            <button
              type="button"
              onClick={() => { setAddingIncident(true); setEditingIncident(null); }}
              className="ml-auto inline-flex items-center gap-1 h-8 rounded-lg border border-orange-200 bg-orange-50 hover:bg-orange-100 text-orange-700 px-2.5 text-xs font-bold"
              data-testid="incident-add"
            >
              <Plus className="h-3.5 w-3.5" /> Añadir
            </button>
          )}
        </div>

        {(addingIncident || editingIncident) && (
          <IncidentForm
            initial={editingIncident}
            saving={savingIncident}
            onCancel={() => { setAddingIncident(false); setEditingIncident(null); }}
            onSave={submitIncident}
          />
        )}

        <div className="mt-2 space-y-2" data-testid="incidents-list">
          {incidents.length === 0 && !addingIncident && (
            <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-4 text-xs text-neutral-500 text-center">
              Sin incidencias registradas.
            </div>
          )}
          {incidents.map((inc) => (
            <IncidentRow
              key={inc.id}
              inc={inc}
              onEdit={(x) => { setEditingIncident(x); setAddingIncident(false); }}
              onDelete={deleteIncident}
            />
          ))}
        </div>
      </section>

      {/* V1.83 · Cronograma de clases del alumno (una fila por cita). */}
      <StudentClassChronogram studentId={sid} student={student} />

      <PlannerDialog
        open={plannerOpen}
        student={student}
        onClose={() => setPlannerOpen(false)}
        onAccepted={() => { /* appointment created; no local reload needed */ }}
      />
    </div>
  );
}
